import isNumber from 'lodash/isNumber';

import {formatPercent} from 'common/report/util/funnels/utils';

import {ChartableStep, CountType, Step} from './types';

export function getYInterval(maxStepCount) {
  if (maxStepCount === 0) {
    return 0;
  } else if (maxStepCount < 5) {
    return maxStepCount + (maxStepCount % 2);
  }

  // Create nice intervals that take advantage of the fact that there are 6
  // visible subdivisions on the y axis.
  const interval = maxStepCount / 7;

  // Determine a nice interval thats some power of 10 multiple of 1, 2, or 5
  const exp = Math.floor(Math.log(interval) / Math.log(10));
  const f = interval / Math.pow(10, exp);
  let niceInterval = Math.pow(10, exp + 1);
  if (f < 1.1) {
    niceInterval = Math.pow(10, exp);
  } else if (f <= 2) {
    niceInterval = 2 * Math.pow(10, exp);
  } else if (f <= 3) {
    niceInterval = 3 * Math.pow(10, exp);
  } else if (f <= 5) {
    niceInterval = 5 * Math.pow(10, exp);
  }

  // Let the first label be the next niceInterval greater than maxStepCount
  return niceInterval;
}

export const minimumBarHeight = 3;

/**
 * @return percentage height of the bar between 0 - 100
 */
export function barHeight(
  value: number,
  stepIndex: number,
  barIndex: number,
  overallSteps: Step[],
  countType: CountType,
  maxOverallStepValue: number,
): number {
  if (countType === CountType.RELATIVE && isNumber(stepIndex) && isNumber(barIndex)) {
    // If ndx = 0 and value = 0, all bars should be minimum height
    if (stepIndex === 0) {
      return value === 0 ? minimumBarHeight : 100;
    }
    const maxSegmentStepValue = overallSteps[0].data[barIndex].value;
    return (value / maxSegmentStepValue) * 100 || 0;
  } else {
    return (value / maxOverallStepValue) * 100 || 0;
  }
}

export function dropOffBarHeight(
  step: Step,
  idx: number,
  barIdx: number,
  overallSteps: Step[],
  countType: CountType,
  maxOverallStepValue: number,
): number {
  if (idx === 0) {
    return 0;
  }
  const currStepBarHeight =
    barHeight(step.data[barIdx].value, idx, barIdx, overallSteps, countType, maxOverallStepValue) / 100;
  const prevStepBarHeight =
    barHeight(overallSteps[idx - 1].data[barIdx].value, idx, barIdx, overallSteps, countType, maxOverallStepValue) /
    100;
  return (prevStepBarHeight - currStepBarHeight) * 100;
}

/** precalculate the height of each bar */
export function chartableSteps(steps: Step[], countType: CountType, maxOverallStepValue: number): ChartableStep[] {
  // temporarily remove blank steps
  const blankSteps = [];
  steps = steps.filter((step, index) => {
    if (step.empty) {
      blankSteps.push({step, index});
      return false;
    }
    return true;
  });

  const chartableSteps = steps.map((step, stepIdx) => {
    return {
      ...step,
      data: step.data.map((bar, barIdx) => {
        const convertedHeight = barHeight(bar.value, stepIdx, barIdx, steps, countType, maxOverallStepValue);
        const dropOffHeight = dropOffBarHeight(step, stepIdx, barIdx, steps, countType, maxOverallStepValue);
        let convertedHeightOrMinimum = Math.max(convertedHeight, minimumBarHeight);
        let dropOffHeightOrMinimum = Math.max(dropOffHeight, minimumBarHeight);
        if (bar.value === steps[0].data[barIdx].value) {
          // no drop off from first step
          convertedHeightOrMinimum = 100;
          dropOffHeightOrMinimum = 0;
        } else if (stepIdx !== 0 && dropOffHeightOrMinimum + convertedHeightOrMinimum > 100) {
          // ensures (converted height + drop off height) <= 100 after the first step
          if (dropOffHeightOrMinimum !== dropOffHeight) {
            const difference = dropOffHeightOrMinimum - dropOffHeight;
            convertedHeightOrMinimum -= difference;
          } else if (convertedHeightOrMinimum !== convertedHeight) {
            const difference = convertedHeightOrMinimum - convertedHeight;
            dropOffHeightOrMinimum -= difference;
          }
        }
        return {
          ...bar,
          dropOffBarHeight: dropOffHeightOrMinimum,
          convertedBarHeight: convertedHeightOrMinimum,
        };
      }),
    };
  });

  blankSteps.forEach(({index, step}) => chartableSteps.splice(index, 0, step));
  return chartableSteps;
}

export function getMaxStepValue(steps: Step[]) {
  const stepValues = steps.flatMap((step) => step.data.map((barData) => barData.value));
  return stepValues.length > 0 ? Math.max(...stepValues) : 0;
}

export function getLastStepValue(steps: Step[]) {
  const lastStep = steps.filter((step) => !step.empty).slice(-1)[0];
  return lastStep ? lastStep.data[0]?.value : 0;
}

export function getOverallConversionRate(steps: Step[]) {
  return formatPercent(getLastStepValue(steps) / getMaxStepValue(steps));
}
